import 'remote-web-worker';

import { message } from 'antd';
import { useCallback, useContext, useEffect, useRef } from 'react';

import Button from '@/components/common/Button';
import { RightContext } from '@/components/RightProvider/context';
import { usePartial } from '@/hooks/usePartial';
import UserStore, { CurrentVip } from '@/store/User';
import { AIFunction } from '@/utils/const';
import { t } from '@/utils/helper/intl';

import CommonModal, { PaymentModal } from '../index';
import { createNewVoice, CreateVoiceParams } from '../service';
import DraggerContent, { DraggerContentRef, UrlData } from './components/draggerContent';
import AvatarForm, { defaultVoiceData, VoiceFormData } from './components/voiceForm';

interface CreateVoiceModal {
  onClose?: () => void;
  onConfirm?: () => void;
}
function isAvailable(currentVip: CurrentVip) {
  try {
    if (!currentVip) {
      console.error('currentVip无法获取');
      return false;
    }
    if (currentVip?.voiceSeat?.available <= 0) {
      if (currentVip.key === 'freeVip') {
        PaymentModal.show({
          feature: AIFunction.VoiceClone,
          customTip: `${t('merror.sub.noseatvoice')}`,
        });
      } else {
        message.warning(t('Voice-slots-are-full-Please-remove-older-custom-vo'));
      }

      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

const Content = (props: CreateVoiceModal) => {
  const rightContext = useContext(RightContext);
  const [btnState, setBtnState] = usePartial({
    disabled: true,
    loading: false,
  });
  const formData = useRef<VoiceFormData>(defaultVoiceData);
  const urlData = useRef<UrlData>({
    url: '',
    name: '',
    type: 'video',
    detailType: '',
    duration: 0,
    size: 0,
    format: '',
  });
  useEffect(() => {
    rightContext.withUpdate();
  }, []);
  const draggerRef = useRef<DraggerContentRef>(null);

  const onCancel = () => {
    props.onClose?.();
  };
  const onConfirm = async () => {
    if (!formData.current.voiceName) {
      message.warning(t('please-enter-a-name'));
      return;
    }
    if (!formData.current.gender) {
      message.warning(t('please-check-a-gender'));
      return;
    }
    if (!formData.current.language) {
      message.warning(t('d17b808544adb75ed3d855ea5e821d58'));
      return;
    }
    try {
      setBtnState({
        loading: true,
      });
      const isValid = isAvailable(rightContext?.currentVip);
      if (!isValid) {
        return;
      }
      const formatType = ['en', 'es', 'fr', 'de'].includes(formData.current.language)
        ? 5
        : 6;
      let params: CreateVoiceParams = {
        voiceUrl: urlData.current.url,
        title: formData.current.voiceName,
        gender: formData.current.gender as 1 | 2,
        voiceCloneType: formatType,
        textLang: formData.current.language,
        voicePreviewText:
          'Hello there! Here’s a quick preview of this voice. If it matches your vibe, start creating with it',
        needTemplate: true,
        needSaveAsset: false,
      };
      const result = await createNewVoice(params);
      if (result.success) {
        message.success(t('merror.tip.submitSuccess'));
        rightContext.withUpdate();
        props.onClose?.();
        props.onConfirm?.();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setBtnState({
        loading: false,
      });
    }
  };
  const getAudioData = (data: UrlData) => {
    urlData.current = data;
    setBtnState({
      disabled: !data.url,
    });
  };
  const handleClear = () => {
    urlData.current = {
      url: '',
      name: '',
      type: 'video',
      detailType: '',
      duration: 0,
      size: 0,
      format: '',
    };
    setBtnState({
      disabled: !urlData.current.url,
    });
  };

  const changeForm = useCallback((data: VoiceFormData) => {
    formData.current = data;
    setBtnState({
      disabled: !urlData.current.url,
    });
  }, []);
  return (
    <div className="flex flex-col">
      <DraggerContent
        t={t}
        ref={draggerRef}
        onChange={getAudioData}
        onClear={handleClear}
      />
      <AvatarForm onChange={changeForm} />
      <div className="w-full h-[64px] flex items-center justify-center">
        <div className="flex flex-row items-center gap-[8px]">
          <Button
            onClick={onCancel}
            style={{
              border: '1px solid #1A1A1A1A',
            }}
            className="!bg-[#fff] !font-normal !text-[#111] !px-[35px]  min-w-[142px]"
          >
            {t('merror.operate.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            className="!font-normal  !px-[35px]  min-w-[142px]"
            disabled={btnState.disabled}
            loading={btnState.loading}
          >
            {t('merror.operate.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};
const Title = () => {
  const currentVip = UserStore((store) => store.currentVip);
  return (
    <div className="flex items-center">
      <span className="text-[20px] font-[500] text-[#000] pr-[8px]">{t('bc.vc')}</span>
      <span className="bg-common-modal-seat-tag-bg px-[8px] inline-block rounded-[4px] text-[12px] text-common-modal-seat-tag-color">
        <span className="font-number">
          {currentVip.voiceSeat.used}/{currentVip.voiceSeat.total}
        </span>
        &nbsp;{t('used')}
      </span>
    </div>
  );
};
export function createVoiceModalShow(options: CreateVoiceModal = {}) {
  CommonModal.show((t) => ({
    width: 690,
    title: <Title />,
    footer: false,
    customPadding: {
      x: 16,
      y: 0,
    },
    content: (close) => (
      <Content
        onConfirm={options.onConfirm}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
